/**
 * recoveryRuntime.ts — Runtime Bridge v18.14.0
 * 
 * Conecta el Recovery Center UI con el backend Express real.
 * Implementa:
 *   - Token local seguro con nonce criptográfico
 *   - Detección real de backups en filesystem
 *   - Generación real vía scripts (JSON, Excel, Incremental)
 *   - Descargas con blob seguro
 *   - Checkpoints con metadata
 *   - Restore preview (dry-run)
 * 
 * NO toca: dashboards, KPIs, tracking, planeación, Supabase SQL, Firebase auth.
 */

import { supabase } from '../supabase';

// --- Tipos ---
export interface BackupItem {
  name: string;
  type: 'JSON' | 'EXCEL' | 'INCREMENTAL' | 'ZIP' | 'CHECKPOINT';
  size: number;
  date: string;
  path: string;
}

export interface RuntimeState {
  safeMode: string;
  reconfigMode: string;
  features?: {
    safeMode?: { status: string; version: string };
    reconfigMode?: { status: string };
  };
  timestamp?: string;
  version?: string;
}

export interface AuditEntry {
  date: string;
  user: string;
  action: string;
  [key: string]: unknown;
}

export interface BackupListResponse {
  backups: BackupItem[];
  runtimeState: RuntimeState;
  auditLog: AuditEntry[];
}

export interface GenerateResponse {
  success: boolean;
  message: string;
  output?: string;
  error?: string;
}

export interface CheckpointItem {
  name: string;
  size: number;
  date: string;
  path: string;
  metadata?: {
    name: string;
    timestamp: string;
    notes: string;
    version: string;
    hash: string;
    runtime_state: {
      safeMode: string;
      reconfigMode: string;
      environment: string;
    };
  } | null;
}

export interface RestoreReport {
  timestamp: string;
  mode: string;
  source: string;
  tables: Record<string, {
    status: string;
    source_count?: number;
    current_count?: number;
    to_upsert?: number;
    conflicts?: number;
    conflict_details?: unknown[];
  }>;
}

// --- Estado interno del bridge ---
let _localToken: string | null = null;
let _tokenExpiresAt: number = 0;

const RECOVERY_API_BASE = '/api/backups';
export const SAFE_HOSTING_MESSAGE = 'Disponible únicamente en entorno local seguro';
export const RECOVERY_AUTH_REQUIRED_MESSAGE = 'Recovery Center requiere sesión real de Supabase con rol SUPER_ADMIN';

export const isRecoverySafeHostingMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname || '';
  return hostname.includes('web.app') || hostname.includes('firebaseapp.com');
};

const assertRuntimeAvailable = (): void => {
  if (isRecoverySafeHostingMode()) {
    throw new Error(SAFE_HOSTING_MESSAGE);
  }
};

// --- Helpers internos ---
const isTokenExpired = (): boolean => {
  return !_localToken || Date.now() > _tokenExpiresAt;
};

const getAuthenticatedSupabaseToken = async (): Promise<string> => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error(RECOVERY_AUTH_REQUIRED_MESSAGE);
  }

  _localToken = token;
  _tokenExpiresAt = session?.expires_at ? session.expires_at * 1000 : Date.now() + 3600000;
  return token;
};

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getAuthenticatedSupabaseToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const handleResponse = async <T>(res: Response): Promise<T> => {
  const contentType = res.headers.get('content-type') || '';
  
  // Detección de fallbacks accidentales que devuelven HTML index.html (como Firebase Hosting o Vite SPA Fallback)
  if (!contentType.includes('application/json')) {
    const text = await res.text();
    if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
      throw new Error(SAFE_HOSTING_MESSAGE);
    }
    throw new Error(`Respuesta inválida del servidor (Content-Type: ${contentType || 'desconocido'}).`);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(body.error || `Error ${res.status}`);
  }
  return res.json();
};

// ============================================================
// API PÚBLICA DEL BRIDGE
// ============================================================

/**
 * Inicializa credenciales reales de Supabase para Recovery Center.
 * Ya no emite ni solicita tokens RCLOCAL_*.
 */
export const initLocalToken = async (): Promise<string> => {
  assertRuntimeAvailable();

  if (_localToken && !isTokenExpired()) {
    return _localToken;
  }

  return getAuthenticatedSupabaseToken();
};

/**
 * Lista TODOS los backups reales del filesystem.
 * Devuelve: archivos JSON, Excel, incrementales, ZIPs, checkpoints, runtime state y audit log.
 */
export const listBackups = async (): Promise<BackupListResponse> => {
  assertRuntimeAvailable();
  await initLocalToken();
  const res = await fetch(`${RECOVERY_API_BASE}/list`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse<BackupListResponse>(res);
};

/**
 * Genera un backup real ejecutando el script correspondiente.
 * @param type - 'json' | 'excel' | 'incremental'
 */
export const generateBackup = async (type: 'json' | 'excel' | 'incremental'): Promise<GenerateResponse> => {
  assertRuntimeAvailable();
  await initLocalToken();
  const res = await fetch(`${RECOVERY_API_BASE}/generate/${type}`, {
    method: 'POST',
    headers: await getAuthHeaders(),
  });
  return handleResponse<GenerateResponse>(res);
};

/**
 * Descarga un archivo de backup usando blob seguro.
 * Soporta archivos grandes. Dispara descarga automática en el navegador.
 */
export const downloadBackupFile = async (filePath: string, fileName?: string): Promise<void> => {
  assertRuntimeAvailable();
  await initLocalToken();
  const url = `${RECOVERY_API_BASE}/download?path=${encodeURIComponent(filePath)}`;
  const res = await fetch(url, {
    headers: await getAuthHeaders(),
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(errorBody.error || 'Error descargando archivo');
  }

  // Verificar si es directorio (respuesta JSON) o si es fallback HTML accidental
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('text/html')) {
    const text = await res.text();
    if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
      throw new Error(SAFE_HOSTING_MESSAGE);
    }
  }

  if (contentType.includes('application/json')) {
    // Es un directorio incremental — descargar como JSON
    const data = await res.json();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    triggerBlobDownload(blob, fileName || 'incremental_data.json');
    return;
  }

  // Archivo normal: descargar como blob
  const blob = await res.blob();
  const downloadName = fileName || filePath.split(/[\\/]/).pop() || 'backup_download';
  triggerBlobDownload(blob, downloadName);
};

/**
 * Dispara descarga de un blob en el navegador.
 */
const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
};

/**
 * Crea un checkpoint con metadata, hash y runtime_state.
 */
export const createCheckpoint = async (name: string, notes: string = ''): Promise<{ success: boolean; message: string; file?: string }> => {
  assertRuntimeAvailable();
  await initLocalToken();
  const res = await fetch(`${RECOVERY_API_BASE}/checkpoints`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ name, notes }),
  });
  return handleResponse(res);
};

/**
 * Lista todos los checkpoints existentes con su metadata.
 */
export const listCheckpoints = async (): Promise<{ checkpoints: CheckpointItem[] }> => {
  assertRuntimeAvailable();
  await initLocalToken();
  const res = await fetch(`${RECOVERY_API_BASE}/checkpoints/list`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse(res);
};

/**
 * Ejecuta un restore en modo preview (dry-run).
 * NO modifica datos. Solo analiza y devuelve reporte.
 */
export const restorePreview = async (tables?: string[]): Promise<{ success: boolean; report: RestoreReport }> => {
  assertRuntimeAvailable();
  await initLocalToken();
  const res = await fetch(`${RECOVERY_API_BASE}/restore-safe`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ mode: 'dry-run', tables }),
  });
  return handleResponse(res);
};

/**
 * Ejecuta un restore real (commit).
 * ⚠️ MODIFICA la base de datos. Usar con precaución.
 * Requiere rol SUPER_ADMIN explícito.
 */
export const restoreCommit = async (tables?: string[], userRole?: string): Promise<{ success: boolean; report: RestoreReport }> => {
  assertRuntimeAvailable();

  const RESTORE_ALLOWED_ROLES = ['SUPER_ADMIN'] as const;
  if (!userRole || !RESTORE_ALLOWED_ROLES.includes(userRole as typeof RESTORE_ALLOWED_ROLES[number])) {
    throw new Error(`ACCESO DENEGADO: restoreCommit() requiere rol SUPER_ADMIN. Rol recibido: "${userRole ?? 'ninguno'}"`);
  }
  await initLocalToken();
  const res = await fetch(`${RECOVERY_API_BASE}/restore-safe`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ mode: 'commit', tables }),
  });
  return handleResponse(res);
};

/**
 * Contadores derivados de la lista de backups.
 */
export const getBackupCounts = (backups: BackupItem[]): {
  json: number;
  excel: number;
  incremental: number;
  zip: number;
  checkpoint: number;
  total: number;
} => {
  const json = backups.filter(b => b.type === 'JSON').length;
  const excel = backups.filter(b => b.type === 'EXCEL').length;
  const incremental = backups.filter(b => b.type === 'INCREMENTAL').length;
  const zip = backups.filter(b => b.type === 'ZIP').length;
  const checkpoint = backups.filter(b => b.type === 'CHECKPOINT').length;
  return { json, excel, incremental, zip, checkpoint, total: backups.length };
};

/**
 * Verifica si el bridge tiene un token válido activo.
 */
export const isAuthenticated = (): boolean => {
  return !isTokenExpired();
};

/**
 * Limpia el token local (logout del bridge).
 */
export const clearLocalToken = (): void => {
  _localToken = null;
  _tokenExpiresAt = 0;
};
